import React from "react";

interface Props {
  children: React.ReactNode;
  /** Texto exibido quando ocorre um erro de renderização. */
  message?: string;
  /** Chamado ao clicar em "Fechar" / "Tentar novamente". */
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Captura erros de renderização de uma subárvore e mostra uma mensagem local
 * em vez de derrubar a rota inteira (evita a tela "Unexpected Application
 * Error!" do React Router e a necessidade de dar F5).
 */
class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Mantém o rastro no console para depuração (inclui component stack).
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
    this.props.onReset?.();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="w-full flex flex-col items-center justify-center gap-4 p-10 text-center">
        <span className="text-base text-[#000] font-bold">
          {this.props.message ?? "Erro ao carregar esta etapa."}
        </span>
        <span className="text-sm text-[#666666] font-normal max-w-md">
          Tente novamente. Se o problema persistir, feche e reabra a operação.
        </span>
        <button
          type="button"
          className="h-9 px-4 flex items-center justify-center bg-[#0A4984] text-sm text-[#fff] font-bold rounded-full"
          onClick={this.handleReset}
        >
          Tentar novamente
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
